import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import RecipeRegisterFormStep1 from "../../forms/recipes/recipeRegisterFormStep1";
import RecipeRegisterFormStep2 from "../../forms/recipes/recipeRegisterFormStep2";
import RecipeNutritionalInfoForm from "../../forms/recipes/recipeNutritionalInfoForm";
import Stepper from "../../../../components/lib/stepperHeader";

export const STEP = {
    BASIC: 0,
    DETAILS: 1,
    NUTRITION: 2,
    SUMMARY: 3,
};

const steps = [
    { key: STEP.BASIC, label: "Básico" },
    { key: STEP.DETAILS, label: "Detalhes" },
    { key: STEP.NUTRITION, label: "Tabela Nutricional" },
    { key: STEP.SUMMARY, label: "Resumo" },
];

const Info = ({ label, value }) => (
    <div className="flex justify-between border-b pb-1">
        <span className="font-medium">{label}</span>
        <span>{value || "-"}</span>
    </div>
);

const RecipeRegisterStepper = ({ onFinish, onCancel, onDirty, isSubmitting }) => {
    const [step, setStep] = useState(STEP.BASIC);

    const formik = useFormik({
        initialValues: {
            // STEP 1
            imageFile: null,
            imagePreview: null,
            name: "",
            price: "",
            online_price: "",
            sku: "",
            active: false,
            needsPreparation: true,

            // STEP 2
            description: "",
            observations: "",
            ingredients: [],

            // STEP 3
            nutrition: [
                { nutrient: 'Valor Energético', valueInPortion: '', dailyValue: '' },
                { nutrient: 'Carboidratos', valueInPortion: '', dailyValue: '' },
                { nutrient: 'Proteínas', valueInPortion: '', dailyValue: '' },
                { nutrient: 'Gorduras Totais', valueInPortion: '', dailyValue: '' },
                { nutrient: 'Fibra Alimentar', valueInPortion: '', dailyValue: '' },
                { nutrient: 'Sódio', valueInPortion: '', dailyValue: '' },
            ],
        },
        validationSchema: Yup.object({
            name: Yup.string().required("Nome é um campo obrigatório")
                .max(50, "O nome deve ter no máximo 50 caracteres"),
            price: Yup.number().required("Preço é um campo obrigatório"),
            sku: Yup.string()
                .max(30, 'O código SKU deve ter no máximo 30 caracteres')
                .trim('O campo não deve conter espaços no início ou fim')
                .matches(/^[\p{L}0-9()\-\s*#]*$/u, 'O campo não deve conter caracteres especiais \n Caracteres aceitos: letras, números, espaços, (), -, * e #'),
            imageFile: Yup.mixed()
                .nullable()
                .test("fileSize", "A foto do combo deve ter no máximo 25MB", (value) => {
                    if (!value) return true;
                    return value.size <= 25 * 1024 * 1024;
                })
                .test("fileType", "Formato inválido. Use JPEG, JPG ou PNG", (value) => {
                    if (!value) return true;
                    return ["image/jpeg", "image/png", "image/jpg"].includes(value.type);
                }),
        }),
        onSubmit: async () => {
            if (step < STEP.SUMMARY) {
                setStep(s => s + 1);
                return;
            }

            const {
                imageFile,
                nutrition,
                ingredients,
                ...recipeData
            } = formik.values;

            const formattedIngredients = ingredients.map(item =>
                `${item.name} x${item.amount}`).join(', ');

            await onFinish({
                recipe: {
                    ...recipeData,
                    ingredientsList: formattedIngredients
                },
                nutrition,
                image: imageFile,
            });
        }
    });

    return (
        <form onSubmit={formik.handleSubmit} onChange={onDirty} className="flex flex-col h-full">
            <Stepper
                steps={steps}
                currentStep={step}
            />

            <div className="flex-1 overflow-y-auto pr-1 custom-scrollbar">
                {step === STEP.BASIC && <RecipeRegisterFormStep1 formik={formik} />}
                {step === STEP.DETAILS && <RecipeRegisterFormStep2 formik={formik} />}
                {step === STEP.NUTRITION && (
                    <RecipeNutritionalInfoForm formik={formik} />
                )}
                {step === STEP.SUMMARY && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-4">
                        <div className="space-y-6">
                            <div className="w-full aspect-video md:aspect-square lg:aspect-video bg-gray-50 rounded-xl overflow-hidden border border-gray-200 shadow-sm relative flex items-center justify-center">
                                {formik.values.imagePreview ? (
                                    <img
                                        src={formik.values.imagePreview}
                                        className="w-full h-full object-contain"
                                        alt="Preview"
                                    />
                                ) : (
                                    <span className="text-gray-400 text-sm">Sem imagem</span>
                                )}
                            </div>

                            <div className="bg-gray-50 p-4 rounded-xl space-y-3 border border-gray-200">
                                <h4 className="font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-2">Informações Básicas</h4>
                                <Info label="Nome" value={formik.values.name} />
                                <Info label="Preço" value={formik.values.price ? `R$ ${formik.values.price}` : ""} />
                                <Info label="Preço Online" value={formik.values.online_price ? `R$ ${formik.values.online_price}` : ""} />
                                <Info label="Categoria" value={formik.values.category} />
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="bg-gray-50 p-4 rounded-xl space-y-3 border border-gray-200">
                                <h4 className="font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-2">Detalhes & Estoque</h4>
                                <Info label="SKU" value={formik.values.sku} />
                                <div className="grid grid-cols-2 gap-4 pt-2">
                                    <div className="text-center p-2 bg-white rounded border">
                                        <span className="block text-xs text-gray-500">Ativo</span>
                                        <span className={`font-medium ${formik.values.active ? "text-green-600" : "text-red-500"}`}>
                                            {formik.values.active ? "Sim" : "Não"}
                                        </span>
                                    </div>
                                    <div className="text-center p-2 bg-white rounded border">
                                        <span className="block text-xs text-gray-500">Preparo</span>
                                        <span className="font-medium text-gray-700">
                                            {formik.values.needsPreparation ? "Sim" : "Não"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <span className="text-sm font-medium text-gray-700 block mb-1">Descrição</span>
                                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-200 min-h-[60px]">
                                        {formik.values.description || <span className="italic text-gray-400">Nenhuma descrição</span>}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700 block mb-1">Observações</span>
                                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-200 min-h-[60px]">
                                        {formik.values.observations || <span className="italic text-gray-400">Nenhuma observação</span>}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="col-span-3 border rounded-xl p-4">
                            <h4 className="font-semibold mb-2">Lista de Ingredientes</h4>
                            <div className="space-y-1">
                                {formik.values.ingredients.map((item, idx) => (
                                    <div key={idx} className="text-sm flex justify-between">
                                        <span>{item.name}</span>
                                        <span className="font-mono">x {item.amount}</span>
                                    </div>
                                ))}
                                {formik.values.ingredients.length === 0 && <p className="text-gray-400 text-sm">Nenhum ingrediente selecionado.</p>}
                            </div>
                        </div>

                        <div className="col-span-1 md:col-span-2 mt-2">
                            <div className="border border-gray-200 rounded-xl overflow-hidden">
                                <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                                    <h4 className="font-semibold text-gray-700">Tabela Nutricional</h4>
                                </div>
                                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {formik.values.nutrition.map((row, index) => (
                                        <div key={index} className="flex justify-between items-center bg-gray-50 p-2 rounded border border-gray-100">
                                            <span className="text-sm font-medium text-gray-600">{row.nutrient}</span>
                                            <span className="text-sm font-bold text-gray-800">
                                                {row.valueInPortion || "-"}
                                                <span className="text-xs font-normal text-gray-500 ml-1">
                                                    ({row.dailyValue || "0"}%)
                                                </span>
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                )}
            </div>

            <div className="flex justify-between mt-8">
                <button
                    type="button"
                    onClick={step === STEP.BASIC ? onCancel : () => setStep(step - 1)}
                    className="px-4 py-2 rounded-xl border"
                >
                    {step === STEP.BASIC ? "Cancelar" : "Voltar"}
                </button>

                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2 rounded-xl bg-blue-600 text-white"
                >
                    {step === STEP.SUMMARY ? "Concluir" : "Avançar"}
                </button>
            </div>
        </form>
    );
};

export default RecipeRegisterStepper;
